import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faEnvelope, faPhone, faMapMarkerAlt,
  faFileInvoice, faCalendarAlt, faDollarSign,
  faCheckCircle, faExclamationTriangle, faPen,
  faGraduationCap, faInfoCircle, faCheck,
  faTimes, faClock, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import InvoiceView from './InvoiceView';

// ============================================================
// CACHE MÉMOIRE (remplace Dexie)
// ============================================================
const memoryCache = new Map();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h

function getCachedProfile(key) {
  const entry = memoryCache.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
    return entry.data;
  }
  return null;
}

function setCachedProfile(key, data) {
  memoryCache.set(key, { data, timestamp: Date.now() });
}

function getStaleProfile(key) {
  const entry = memoryCache.get(key);
  return entry ? entry.data : null;
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES (Map globale)
// ============================================================
const pendingFetches = new Map();

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ AVEC SIGNAL D'ANNULATION
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// URLs API
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}client/api_client_profile.php`;
const INSCRIPTION_API_URL = `${API_CONFIG2.apiUrl}inscription/api_inscription.php`;
const SESSION_API_URL = `${API_CONFIG2.apiUrl}session/api_session.php`;
const FORMATION_API_URL = `${API_CONFIG2.apiUrl}formation/api_formation.php`;

// ============================================================
// COMPOSANT FOND DE PARTICULES (inchangé)
// ============================================================
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animFrame = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const initParticles = () => {
      particles.current = Array.from({ length: 60 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 1,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const pts = particles.current;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.25)';
        ctx.fill();

        for (let j = i + 1; j < pts.length; j++) {
          const p2 = pts[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${0.12 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animFrame.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    animFrame.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.6,
      }}
    />
  );
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ClientProfile = ({ idcl }) => {
  const [client, setClient] = useState(null);
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [showInscriptionModal, setShowInscriptionModal] = useState(false);
  const [submittingInscription, setSubmittingInscription] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const [inscriptionForm, setInscriptionForm] = useState({
    idcl: idcl,
    id_formation: '',
    id_session: '',
    nom_formation: '',
    date_debut: '',
    date_fin: ''
  });

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // FONCTION DE FETCH AVEC DÉDOUBLONNAGE, CACHE MÉMOIRE ET GESTION 429
  // ============================================================
  const safeFetch = useCallback(async (url, options = {}, forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const key = url; // Utilisation de l'URL comme clé de cache (simplifiée)

    // Dédoublonnage : si une requête identique est en cours, on attend son résultat
    const requestKey = forceRefresh ? `${key}_force` : key;
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        return result;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limite de tentatives
    if (retryCount > 5) {
      throw new Error('Trop de tentatives. Veuillez réessayer plus tard.');
    }

    const fetchPromise = (async () => {
      try {
        // Vérifier le cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = getCachedProfile(key);
          if (cached) {
            return cached;
          }
        }

        const response = await fetch(url, { ...options, signal: controller.signal });

        // Gestion du 429 : utilisation du cache périmé et backoff exponentiel
        if (response.status === 429) {
          const stale = getStaleProfile(key);
          if (stale) {
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return safeFetch(url, options, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = getStaleProfile(key);
          if (stale) {
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          setCachedProfile(key, data);
          return data;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = getStaleProfile(key);
        if (stale) {
          return stale;
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  // ============================================================
  // RÉCUPÉRATION DU PROFIL
  // ============================================================
  const fetchProfile = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const cacheKey = `profile_${idcl}`;
      const url = `${API_URL}?idcl=${idcl}`;
      const data = await safeFetch(url, {}, forceRefresh);
      if (data && data.status === 'success') {
        setClient(data.client);
        setFactures(data.factures);
      } else if (data && data.status === 'error') {
        throw new Error(data.message || 'Erreur serveur');
      } else {
        // Cas du cache périmé ou sans status
        if (data && data.client) {
          setClient(data.client);
          setFactures(data.factures || []);
        } else {
          throw new Error('Données invalides');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [idcl, safeFetch]);

  // ============================================================
  // RÉCUPÉRATION DES FORMATIONS (avec cache)
  // ============================================================
  const fetchFormations = useCallback(async (forceRefresh = false) => {
    try {
      const url = `${FORMATION_API_URL}?action=fetch&limit=999`;
      const data = await safeFetch(url, {}, forceRefresh);
      if (data && data.status === 'success') {
        setFormations(data.data);
      } else if (data && data.data) {
        setFormations(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement formations', err);
    }
  }, [safeFetch]);

  // ============================================================
  // RÉCUPÉRATION DES SESSIONS (avec cache)
  // ============================================================
  const fetchSessions = useCallback(async (idFormation, forceRefresh = false) => {
    if (!idFormation) {
      setSessions([]);
      return;
    }
    setLoadingSessions(true);
    try {
      const url = `${SESSION_API_URL}?action=list&id_formation=${idFormation}`;
      const data = await safeFetch(url, {}, forceRefresh);
      if (data && data.status === 'success') {
        setSessions(data.data || []);
      } else if (data && data.data) {
        setSessions(data.data || []);
      } else {
        setSessions([]);
      }
    } catch (err) {
      console.error('Erreur chargement sessions', err);
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  }, [safeFetch]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchProfile(false);
    fetchFormations(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pendingFetches.clear();
      memoryCache.clear(); // Nettoyer le cache mémoire à la fin
    };
  }, [fetchProfile, fetchFormations]);

  // ============================================================
  // GESTION DE L'OUVERTURE DU MODAL INSCRIPTION
  // ============================================================
  const openInscriptionModal = (facture) => {
    let formationName = facture.details || '';
    const prefix = 'Achat de la formation : ';
    if (formationName.startsWith(prefix)) {
      formationName = formationName.substring(prefix.length);
    }
    const formation = formations.find(f => f.titre.trim() === formationName.trim());
    const idFormation = formation ? formation.id : '';

    setInscriptionForm({
      idcl: idcl,
      id_formation: idFormation,
      id_session: '',
      nom_formation: formationName,
      date_debut: '',
      date_fin: ''
    });

    // Charger les sessions pour cette formation (avec forceRefresh pour avoir les dernières)
    if (idFormation) {
      fetchSessions(idFormation, true);
    } else {
      setSessions([]);
    }

    setShowInscriptionModal(true);
  };

  // ============================================================
  // GESTION DES CHANGEMENTS DE SESSION
  // ============================================================
  const handleInscriptionChange = (e) => {
    const { name, value } = e.target;
    if (name === 'id_session') {
      const session = sessions.find(s => s.id === parseInt(value));
      if (session) {
        setInscriptionForm(prev => ({
          ...prev,
          id_session: value,
          date_debut: session.debut,
          date_fin: session.fin
        }));
      } else {
        setInscriptionForm(prev => ({
          ...prev,
          id_session: value,
          date_debut: '',
          date_fin: ''
        }));
      }
    } else {
      setInscriptionForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // ============================================================
  // SOUMISSION DE L'INSCRIPTION
  // ============================================================
  const handleInscriptionSubmit = async (e) => {
    e.preventDefault();
    if (!inscriptionForm.id_session) {
      setToast({ show: true, message: 'Veuillez sélectionner une session', variant: 'danger' });
      return;
    }
    setSubmittingInscription(true);
    try {
      const payload = {
        idcl: inscriptionForm.idcl,
        id_formation: inscriptionForm.id_formation,
        id_session: inscriptionForm.id_session
      };
      const data = await fetchJSON(INSCRIPTION_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setShowInscriptionModal(false);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
    } finally {
      setSubmittingInscription(false);
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    }
  };

  // ============================================================
  // AFFICHAGE DE LA MODALE FACTURE
  // ============================================================
  const openInvoiceModal = (id) => {
    setInvoiceId(id);
    setShowInvoiceModal(true);
  };

  // ============================================================
  // ÉTATS DE CHARGEMENT / ERREUR
  // ============================================================
  if (loading) {
    return (
      <>
        <ParticleCanvas />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-secondary mt-2">Chargement du profil...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <ParticleCanvas />
        <div className="text-center py-5 text-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
          <div className="mt-3">
            <button className="monetra-btn primary" onClick={() => fetchProfile(true)}>
              Réessayer
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!client) return null;

  // ============================================================
  // RENDU (inchangé)
  // ============================================================
  return (
    <>
      <ParticleCanvas />
      <style>{`
        /* ============================================================
           STYLES (inchangés, avec légères améliorations responsives)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 18px;
        }
        .client-profile {
          position: relative;
          z-index: 1;
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
        }
        .fade-up {
          opacity: 0;
          animation: fadeInUp 0.5s ease forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 2vw, 1.8rem);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
          margin-bottom: 28px;
          transition: all 0.25s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.08);
          transform: translateY(-2px);
        }
        .profile-header {
          display: flex;
          align-items: center;
          gap: 24px;
          flex-wrap: wrap;
        }
        .avatar-large {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.8rem;
          font-weight: 700;
          box-shadow: 0 0 30px rgba(59, 130, 246, 0.25);
          flex-shrink: 0;
        }
        .profile-info h2 {
          margin: 0 0 6px 0;
          font-size: clamp(1.6rem, 4vw, 2.2rem);
          font-weight: 600;
          letter-spacing: -0.02em;
          background: linear-gradient(180deg, #fff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .profile-info .sub {
          color: var(--text-secondary);
          font-size: clamp(0.85rem, 1.2vw, 1rem);
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 4px;
        }
        .profile-info .sub svg {
          color: var(--accent-blue);
          opacity: 0.8;
          width: 16px;
        }
        .status-badge {
          padding: 4px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          display: inline-block;
        }
        .status-badge.Envoyée {
          background: rgba(96, 165, 250, 0.15);
          color: #60a5fa;
        }
        .status-badge.Payée {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }
        .status-badge.Impayée {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }
        .glass-table {
          width: 100%;
          border-collapse: collapse;
        }
        .glass-table th {
          padding: 14px 12px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table tr {
          transition: background 0.15s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }
        .monetra-btn {
          padding: 8px 16px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
          font-weight: 500;
          cursor: pointer;
          min-height: 40px;
          touch-action: manipulation;
        }
        .monetra-btn:hover {
          background: rgba(255,255,255,0.08);
          transform: scale(1.02);
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: white;
          border: none;
          box-shadow: 0 4px 16px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 15, 31, 0.96);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.08);
          border-radius: var(--radius);
          color: var(--text-primary);
          box-shadow: 0 40px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.2rem 1.5rem;
          background: linear-gradient(135deg, rgba(59,130,246,0.08), transparent);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-input {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          color: var(--text-primary);
          padding: 12px 16px;
          width: 100%;
          transition: border-color 0.2s, background 0.2s;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .monetra-input:disabled, .monetra-input[readonly] {
          opacity: 0.7;
          background: rgba(255,255,255,0.02);
        }
        .monetra-input option {
          background: #1a1a2e;
          color: var(--text-primary);
        }
        .session-summary {
          background: rgba(59,130,246,0.08);
          border-radius: 16px;
          padding: 12px 16px;
          border-left: 4px solid var(--accent-blue);
        }
        .session-summary .label {
          color: var(--text-secondary);
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .session-summary .value {
          font-weight: 500;
          color: var(--text-primary);
        }
        .section-title {
          font-size: clamp(1rem, 1.5vw, 1.2rem);
          font-weight: 600;
          color: var(--accent-blue);
          margin-bottom: 1.2rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .section-title svg {
          opacity: 0.9;
        }
        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 15, 31, 0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
        }
        .toast-item.success { border-left: 4px solid #10b981; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-primary);
          opacity: 0.6;
          font-size: 1.2rem;
          cursor: pointer;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        .d-flex { display: flex; }
        .gap-2 { gap: 0.5rem; }
        .flex-wrap { flex-wrap: wrap; }
        .text-center { text-align: center; }
        .text-secondary { color: var(--text-secondary); }
        .text-danger { color: #ef4444; }
        .text-success { color: #10b981; }
        .fw-bold { font-weight: 600; }
        .me-1 { margin-right: 0.25rem; }
        .mt-2 { margin-top: 0.5rem; }
        .mt-3 { margin-top: 1rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .ms-2 { margin-left: 0.5rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }

        @media (max-width: 768px) {
          .profile-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .glass-card {
            padding: 1.2rem;
          }
          .glass-table th, .glass-table td {
            padding: 10px 8px;
            font-size: 0.75rem;
          }
          .monetra-btn {
            padding: 6px 12px;
            font-size: 0.7rem;
            min-height: 36px;
          }
          .monetra-modal .modal-header {
            padding: 1rem 1.2rem;
          }
          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
          .section-title {
            font-size: 1rem;
          }
        }
        @media (max-width: 480px) {
          .glass-table th, .glass-table td {
            font-size: 0.65rem;
            padding: 6px 4px;
          }
          .monetra-btn {
            font-size: 0.65rem;
            padding: 4px 8px;
            min-height: 32px;
          }
        }
      `}</style>

      <div className="client-profile">
        {/* Carte client */}
        <div className="glass-card fade-up" style={{ animationDelay: '0ms' }}>
          <div className="profile-header">
            <div className="avatar-large">{client.nom?.charAt(0).toUpperCase()}</div>
            <div className="profile-info">
              <h2>{client.nom}</h2>
              <div className="sub">
                <FontAwesomeIcon icon={faEnvelope} /> {client.email}
              </div>
              <div className="sub">
                <FontAwesomeIcon icon={faPhone} /> {client.telephone || 'Non renseigné'}
              </div>
              <div className="sub">
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {client.adresse || 'Non renseignée'}
              </div>
            </div>
          </div>
        </div>

        {/* Liste des factures */}
        <div className="glass-card fade-up" style={{ animationDelay: '150ms' }}>
          <div className="section-title">
            <FontAwesomeIcon icon={faFileInvoice} />
            Factures du client
          </div>

          {factures.length === 0 ? (
            <p className="text-secondary">Aucune facture trouvée pour ce client.</p>
          ) : (
            <div className="table-responsive">
              <table className="glass-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Échéance</th>
                    <th>Montant TTC</th>
                    <th>Statut</th>
                    <th>Détails</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {factures.map(f => (
                    <tr key={f.id}>
                      <td><span className="fw-bold text-secondary">#{f.id}</span></td>
                      <td><span className="text-secondary"><FontAwesomeIcon icon={faCalendarAlt} className="me-1" />{f.date}</span></td>
                      <td><span className="text-secondary">{f.echeance}</span></td>
                      <td><span className="text-secondary"><FontAwesomeIcon icon={faDollarSign} className="me-1" />{parseFloat(f.montantTotal).toFixed(2)} $</span></td>
                      <td><span className={`status-badge ${f.statut}`}>{f.statut}</span></td>
                      <td><span className="text-secondary" style={{ maxWidth: '180px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.details}</span></td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <button className="monetra-btn" onClick={() => openInvoiceModal(f.id)}>
                            <FontAwesomeIcon icon={faFileInvoice} /> Voir
                          </button>
                          {f.statut === 'Payée' && (
                            <button className="monetra-btn primary" onClick={() => openInscriptionModal(f)}>
                              <FontAwesomeIcon icon={faPen} /> S'inscrire
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Facture */}
      <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} size="lg" centered className="monetra-modal">
        <Modal.Body className="p-0">
          {invoiceId && <InvoiceView factureId={invoiceId} />}
        </Modal.Body>
      </Modal>

      {/* Modal Inscription */}
      <Modal show={showInscriptionModal} onHide={() => setShowInscriptionModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faGraduationCap} className="text-primary" />
            <span>Inscription à la formation</span>
            <Badge bg="primary" className="ms-2" style={{ fontSize: '0.7rem', fontWeight: 400 }}>
              Nouvelle
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleInscriptionSubmit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Label className="text-secondary small">
                  <FontAwesomeIcon icon={faUser} className="me-1" /> ID Client
                </Form.Label>
                <Form.Control
                  type="text"
                  value={inscriptionForm.idcl}
                  readOnly
                  className="monetra-input"
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="text-secondary small">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" /> Formation
                </Form.Label>
                <Form.Control
                  type="text"
                  value={inscriptionForm.id_formation || '—'}
                  readOnly
                  className="monetra-input"
                />
              </div>
              <div className="col-12">
                <Form.Label className="text-secondary small">
                  <FontAwesomeIcon icon={faGraduationCap} className="me-1" /> Nom de la formation
                </Form.Label>
                <Form.Control
                  type="text"
                  value={inscriptionForm.nom_formation}
                  readOnly
                  className="monetra-input"
                />
              </div>
              <div className="col-12">
                <Form.Label className="text-secondary small">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" /> Session <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="id_session"
                  value={inscriptionForm.id_session}
                  onChange={handleInscriptionChange}
                  required
                  className="monetra-input"
                  disabled={loadingSessions}
                >
                  <option value="">-- Sélectionner une session --</option>
                  {loadingSessions ? (
                    <option value="" disabled>Chargement des sessions...</option>
                  ) : sessions.length === 0 ? (
                    <option value="" disabled>Aucune session disponible</option>
                  ) : (
                    sessions.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.mois} (du {s.debut} au {s.fin})
                      </option>
                    ))
                  )}
                </Form.Select>
                {loadingSessions && (
                  <div className="text-center mt-2">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span className="ms-2 text-secondary">Chargement des sessions...</span>
                  </div>
                )}
                {!loadingSessions && sessions.length === 0 && inscriptionForm.id_formation && (
                  <div className="text-warning mt-1 small">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    Aucune session disponible pour cette formation.
                  </div>
                )}
              </div>
              {inscriptionForm.id_session && (
                <div className="col-12 session-summary">
                  <div className="row">
                    <div className="col-6">
                      <div className="label">Date de début</div>
                      <div className="value">{inscriptionForm.date_debut || '—'}</div>
                    </div>
                    <div className="col-6">
                      <div className="label">Date de fin</div>
                      <div className="value">{inscriptionForm.date_fin || '—'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowInscriptionModal(false)}>
              <FontAwesomeIcon icon={faTimes} className="me-1" /> Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="monetra-btn primary"
              disabled={submittingInscription || !inscriptionForm.id_session || loadingSessions}
            >
              {submittingInscription ? (
                <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Enregistrement...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} className="me-1" /> Enregistrer l'inscription</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientProfile;